/**
 * OnlineOrdering SDK
 * ------------------
 * One-line embed for the <nova-orders> online-ordering web component.
 *
 * A host site (WordPress brochure, plain HTML, React, anything) loads this SDK
 * with a single <script> tag. The SDK:
 *   - lazy-loads the nova-orders CDN bundle (CSS + JS) on first use,
 *   - owns the `/orders` sub-path on the host page,
 *   - mounts the ordering experience as a full-viewport overlay when the URL is
 *     under `/orders`, and hides it (revealing the host page) otherwise.
 *
 * Framework-agnostic by design — it exposes `window.OnlineOrdering`, so the host
 * wires plain click handlers:
 *
 *   <script src="https://.../online-ordering.js" data-business-ref-id="sns"></script>
 *   <button onclick="OnlineOrdering.open()">Order Online</button>
 *   <button onclick="OnlineOrdering.openLocations()">Find a Location</button>
 *
 * The nova-orders bundle carries its own router (basename = base-path), so once
 * mounted it handles every `/orders/*` route internally.
 */

import { buildOrderingPath, normalizeBase } from './orderingPath';

export interface OnlineOrderingConfig {
  /** Tenant identifier passed to <nova-orders business-ref-id>. Required. */
  businessRefId: string;
  /** Host sub-path the ordering app lives under. Default '/orders'. */
  basePath?: string;
  /** CDN base URL that serves nova-orders.js / nova-orders.css. */
  cdnBase?: string;
  /** Id of the overlay container element. Default 'online-ordering-root'. */
  containerId?: string;
  /**
   * Canary label this tenant is assigned to (e.g. "sns") — which copy of the code
   * serves it. Independent of businessRefId, which identifies the tenant. Omit
   * unless we have told you to set it.
   */
  canaryVersion?: string;
}

const DEFAULT_CDN = 'https://d2rcyj19esn6wq.cloudfront.net/v1';
const DEFAULT_BASE_PATH = '/orders';
const DEFAULT_CONTAINER_ID = 'online-ordering-root';

let cfg: Required<OnlineOrderingConfig> | null = null;
let assetsRequested = false;
let mounted = false;
let popstateBound = false;

const requireCfg = (): Required<OnlineOrderingConfig> => {
  if (!cfg) {
    throw new Error('[OnlineOrdering] call OnlineOrdering.init({ businessRefId }) first');
  }
  return cfg;
};

// Inject the nova-orders CDN script once. Marked with data-nova-orders so a
// second SDK instance / re-init never double-loads it.
//
// The CSS bundle is deliberately NOT injected here. It's the full compiled app
// stylesheet (every Tailwind utility, e.g. `.collapse{visibility:collapse}`),
// and putting it in the HOST's own <head> applies it to the whole document —
// any host class name that happens to match a utility name (Bootstrap's own
// `.collapse`, `.container`, `.hidden`, etc.) gets silently overridden. The
// <nova-orders> element loads its own copy scoped to its Shadow Root instead
// (see nova-orders-element.tsx), so it never leaks onto the host page.
const loadAssets = (): void => {
  if (assetsRequested) return;
  const { cdnBase } = requireCfg();
  assetsRequested = true;

  if (!document.querySelector('script[data-nova-orders]')) {
    const script = document.createElement('script');
    script.src = `${cdnBase}/nova-orders.js`;
    script.defer = true;
    script.setAttribute('data-nova-orders', '');
    document.head.appendChild(script);
  }
};

const getContainer = (): HTMLElement => {
  const { containerId } = requireCfg();
  let el = document.getElementById(containerId);
  if (!el) {
    el = document.createElement('div');
    el.id = containerId;
    // Full-viewport overlay above the host page; hidden until we're on /orders.
    el.style.cssText =
      'position:fixed;inset:0;z-index:2147483000;background:#fff;overflow:auto;display:none;';
    document.body.appendChild(el);
  }
  return el;
};

const isOrderingPath = (): boolean => {
  const { basePath } = requireCfg();
  const { pathname } = window.location;
  return pathname === basePath || pathname.startsWith(`${basePath}/`);
};

// Create the <nova-orders> element once. It upgrades automatically when the CDN
// script defines the custom element, so mounting before the script loads is safe.
const mount = (): void => {
  if (mounted) return;
  const { businessRefId, basePath, cdnBase, canaryVersion } = requireCfg();
  const el = document.createElement('nova-orders');
  el.setAttribute('business-ref-id', businessRefId);
  el.setAttribute('base-path', basePath);
  // Only stamped when the host supplied it — an empty attribute would read as a
  // real (blank) assignment inside the app rather than "not configured".
  if (canaryVersion) el.setAttribute('canary-version', canaryVersion);
  // Tells the element where to load its OWN (shadow-scoped) stylesheet from —
  // see the loadAssets() comment above for why the SDK doesn't load it itself.
  el.setAttribute('cdn-base', cdnBase);
  getContainer().appendChild(el);
  mounted = true;
};

const show = (): void => {
  getContainer().style.display = 'block';
};
const hide = (): void => {
  getContainer().style.display = 'none';
};

// Reconcile the overlay with the current URL — the single source of truth.
// Handles deep links, reloads, and browser back/forward.
const syncToUrl = (): void => {
  if (!cfg) return;
  if (isOrderingPath()) {
    loadAssets();
    mount();
    show();
  } else {
    hide();
  }
};

// Host-side navigation. pushState alone won't reach react-router inside the web
// component (it listens to popstate), so we dispatch a popstate after.
const navigate = (path: string): void => {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
  syncToUrl();
};

const bindPopstate = (): void => {
  if (popstateBound) return;
  popstateBound = true;
  window.addEventListener('popstate', syncToUrl);
};

const OnlineOrdering = {
  /** Configure the SDK. Safe to call once; re-calling updates the config. */
  init(config: OnlineOrderingConfig) {
    if (!config || !config.businessRefId) {
      throw new Error('[OnlineOrdering] init requires a businessRefId');
    }
    cfg = {
      businessRefId: config.businessRefId,
      basePath: normalizeBase(config.basePath || DEFAULT_BASE_PATH),
      cdnBase: (config.cdnBase || DEFAULT_CDN).replace(/\/+$/, ''),
      containerId: config.containerId || DEFAULT_CONTAINER_ID,
      canaryVersion: config.canaryVersion || '',
    };
    bindPopstate();
    // If the page loaded already under /orders (deep link / refresh), mount now.
    syncToUrl();
    return this;
  },

  /** Open the ordering experience at the base path (e.g. "/orders"). */
  open() {
    navigate(buildOrderingPath(requireCfg().basePath, 'home'));
    return this;
  },

  /** Open the location selector at "<base-path>/locations". */
  openLocations() {
    navigate(buildOrderingPath(requireCfg().basePath, 'locations'));
    return this;
  },

  /**
   * Open a specific store's menu at "<base-path>/store/<locationName>".
   * `locationName` is the store slug (e.g. "fl-casselberry-s-hwy-17-92--02ed9f").
   */
  openLocation(locationName: string) {
    const { basePath } = requireCfg();
    if (!locationName) return this.openLocations();
    navigate(buildOrderingPath(basePath, 'store', locationName));
    return this;
  },

  /**
   * Leave the ordering experience and hand the tab back to the host site.
   *
   * This has to be a real navigation rather than `history.pushState`. The
   * document underneath our overlay is the host's page for the ORDERING path
   * (e.g. WordPress's own `/orders` page), not the host's home page — pushState
   * rewrites the URL bar but loads nothing, so hiding the overlay just revealed
   * that same `/orders` document now sitting at "/". Where the host serves
   * `/orders` from a blank template (as it must, so the theme doesn't flash
   * behind us) that is a blank white page.
   *
   * The overlay is deliberately left up: `assign()` commits asynchronously, so
   * hiding first would flash the bare host page before the destination paints.
   *
   * `path` defaults to the host root, overridable for hosts whose landing page
   * lives elsewhere.
   */
  close(path: string = '/') {
    requireCfg();
    window.location.assign(path);
    return this;
  },

  /** True when the current URL is inside the ordering sub-path. */
  isOpen(): boolean {
    return !!cfg && isOrderingPath();
  },
};

// Auto-init from the loading <script>'s data-* attributes so a single script
// line self-configures:
//   <script src=".../online-ordering.js" data-business-ref-id="sns"></script>
const autoInit = (): void => {
  const tag =
    (document.currentScript as HTMLScriptElement | null) ||
    document.querySelector<HTMLScriptElement>('script[data-business-ref-id]');
  const businessRefId = tag?.getAttribute('data-business-ref-id');
  if (!businessRefId) return;
  OnlineOrdering.init({
    businessRefId,
    basePath: tag?.getAttribute('data-base-path') || undefined,
    cdnBase: tag?.getAttribute('data-cdn-base') || undefined,
    containerId: tag?.getAttribute('data-container-id') || undefined,
    canaryVersion: tag?.getAttribute('data-canary-version') || undefined,
  });
};

declare global {
  interface Window {
    OnlineOrdering: typeof OnlineOrdering;
  }
}

if (typeof window !== 'undefined') {
  window.OnlineOrdering = OnlineOrdering;
  autoInit();
}

export default OnlineOrdering;
